import { useParams } from 'react-router';
import { MouseEvent, useCallback, useEffect, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import {
  BackButton,
  EmbeddedView,
  formatCategoriesWithBullet,
  LoadingIndicator,
  useLocale,
  formatEventDate,
  formatEventTime,
  getImageUrl,
  defaultHTMLIframe,
} from '@xolv-mybrightlink/common';
import { FormattedMessage, useIntl } from 'react-intl';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import { useEventSlaStatus } from '../hooks/useEventSla';
import { EventSlaPage } from './EventSlaPage';
import { useBookmarkEvent } from '../hooks/useBookmarkEvent';
import { useFetchEventDetails } from '../hooks/useFetchEventDetails';
import { EventDetailRow } from '../components/EventDetailRow';

export const EventDetailPage = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { eventDatabaseId, categoryId = 'all-events' } = useParams<{
    eventDatabaseId: string;
    categoryId: string;
  }>();
  const eventDetail = useFetchEventDetails(eventDatabaseId);
  const bookmarkEvent = useBookmarkEvent();
  const [eventContent, setEventContent] = useState('');
  const locale = useLocale();
  const intl = useIntl();
  const eventSlaStatus = useEventSlaStatus();

  const getOrganizations = useCallback(() => {
    if (typeof eventDetail !== 'string') {
      return `Organized by ${eventDetail.organizers.nodes
        .map((organizer) => organizer.title)
        .join(' • ')}`;
    }
  }, [eventDetail]);

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollIntoView();
    }
  }, [containerRef]);

  useEffect(() => {
    if (typeof eventDetail !== 'string') {
      const doc = new DOMParser().parseFromString(
        eventDetail.content,
        'text/html',
      );
      doc.querySelectorAll('a').forEach((link) => (link.target = '_top'));
      setEventContent(defaultHTMLIframe(doc.documentElement.innerHTML));
    }
  }, [eventDetail]);

  const onBookmarkClicked = async (event: MouseEvent<SVGSVGElement>) => {
    if (!eventDetail || typeof eventDetail === 'string') {
      return;
    }
    event.stopPropagation();
    await bookmarkEvent(eventDetail.databaseId, categoryId);
  };

  return !eventSlaStatus ? (
    <EventSlaPage />
  ) : (
    <Box
      ref={containerRef}
      sx={{
        height: '100%',
        background: 'white',
      }}
    >
      <Box
        sx={{
          pt: 10,
          pl: 10,
        }}
      >
        <BackButton />
      </Box>
      {typeof eventDetail !== 'string' && (
        <Box
          sx={{
            p: 10,
            background: 'white',
          }}
        >
          <Box
            component='img'
            sx={{
              width: '100%',
              mt: 5,
              height: 400,
              borderRadius: 3,
              boxShadow:
                '0 12px 16px 0 rgba(59, 69, 89, 0.05), 0 8px 10px 0 rgba(59, 69, 89, 0.1)',
              objectFit: 'cover',
            }}
            src={getImageUrl(
              eventDetail?.featuredImage?.node?.mediaDetails?.sizes,
            )}
            alt={eventDetail.title}
          />
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              pt: 6,
            }}
          >
            <Typography
              variant={'h4'}
              sx={{
                fontWeight: 500,
              }}
            >
              {eventDetail.title}
            </Typography>
            {eventDetail.isSaved ? (
              <BookmarkIcon
                sx={{
                  color: '#307fe2',
                  width: 40,
                  height: 40,
                  cursor: 'pointer',
                }}
                onClick={onBookmarkClicked}
              />
            ) : (
              <BookmarkBorderIcon
                sx={{
                  color: '#307fe2',
                  width: 40,
                  height: 40,
                  cursor: 'pointer',
                }}
                onClick={onBookmarkClicked}
              />
            )}
          </Box>
          <Typography
            sx={{
              fontSize: 16,
              pt: 5,
              color: '#626a7a',
            }}
          >
            {getOrganizations()}
          </Typography>
          <Typography
            sx={{
              fontSize: 16,
              fontWeight: 500,
              pt: 5,
            }}
          >
            {formatCategoriesWithBullet(eventDetail?.eventsCategories?.nodes)}
          </Typography>
          <Box
            sx={{
              pt: 5,
              pb: 5,
            }}
          >
            <EventDetailRow
              title={formatEventDate(
                eventDetail.startDate,
                eventDetail.endDate,
                locale,
              )}
              icon={
                <img
                  src='/public/assets/icons/ic_calendar.svg'
                  alt='ic_calendar'
                />
              }
            />
            <EventDetailRow
              title={formatEventTime(
                eventDetail.startDate,
                eventDetail.endDate,
              )}
              icon={
                <img src='/public/assets/icons/ic_clock.svg' alt='ic_clock' />
              }
            />
            {eventDetail.venue && (
              <EventDetailRow
                title={eventDetail.venue.title}
                icon={
                  <img
                    src='/public/assets/icons/ic_location.svg'
                    alt='ic_location'
                  />
                }
              />
            )}
            <EventDetailRow
              title={
                eventDetail.cost ??
                intl.formatMessage({ id: 'resources.event.free' })
              }
              icon={
                <img src='/public/assets/icons/ic_cost.svg' alt='ic_cost' />
              }
            />
          </Box>
          <Typography
            variant={'h4'}
            sx={{
              pt: 5,
              fontWeight: 500,
              color: '#307fe2',
            }}
          >
            <FormattedMessage id='resources.event.about' />
          </Typography>
          <EmbeddedView html={eventContent} bodyMargin='8px 0px' />
          <Typography
            sx={{
              fontSize: 16,
              pt: 10,
              color: '#626a7a',
            }}
          >
            {formatCategoriesWithBullet(eventDetail?.tags?.nodes) || ''}
          </Typography>
        </Box>
      )}
      {eventDetail === 'loading' && (
        <LoadingIndicator
          sx={{
            height: '100%',
          }}
        />
      )}
    </Box>
  );
};
